import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { LayoutRectangle, Platform, ScrollView, View } from "react-native";
import { generatePath, useMatch, useNavigate } from "react-router";
import { Box, Layout as AuroraLayout, Spinner } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { QueryStatus } from "@lookiero/messaging-react";
import { Layout as UiLayout, Sticky, Text, useScreenSize } from "@lookiero/sty-psp-ui";
import { CheckoutItemStatus } from "../../../../domain/checkoutItem/model/checkoutItem";
import { useViewFirstAvailableCheckoutByCustomerId } from "../../../projection/checkout/react/useViewFirstAvailableCheckoutByCustomerId";
import { useViewFiveItemsDiscountByCustomerId } from "../../../projection/checkout/react/useViewFiveItemsDiscountByCustomerId";
import { useViewPricingByCheckoutId } from "../../../projection/pricing/react/useViewPricingByCheckoutId";
import { TrackingPage } from "../../../tracking/tracking";
import { useTrackPageView } from "../../../tracking/useTrackPageView";
import { useTrackPressContinue } from "../../../tracking/useTrackPressContinue";
import { useTrackPressPricing } from "../../../tracking/useTrackPressPricing";
import { FiveItemsDiscountBanner } from "../../components/atoms/fiveItemsDiscountBanner/FiveItemsDiscountBanner";
import { Body } from "../../components/layouts/body/Body";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../i18n/i18n";
import { Routes } from "../../routing/routes";
import { style as summaryStyle } from "./Summary.style";
import { CollapsiblePricing } from "./components/collapsiblePricing/CollapsiblePricing";

interface SummaryProps {
  readonly layout: UiLayout;
  readonly children: ReactNode;
}

const Summary: FC<SummaryProps> = ({ layout: Layout, children }) => {
  const {
    customer: { customerId, country, segment },
    basePath,
    tradename,
  } = useStaticInfo();
  const screenSize = useScreenSize();
  const isDesktopScreen = screenSize === "L";

  const style = useMemo(() => summaryStyle(), []);

  const [pricingCollapsed, setPricingCollapsed] = useState(true);
  const [pricingHeight, setPricingHeight] = useState(0);

  const titleText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.SUMMARY_TITLE });
  const descriptionText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.SUMMARY_DESCRIPTION });
  const submitButtonText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.SUMMARY_SUBMIT_BUTTON });

  const [checkout] = useViewFirstAvailableCheckoutByCustomerId({ customerId });
  const [pricing, pricingStatus] = useViewPricingByCheckoutId({ checkoutId: checkout?.id as string });
  const [fiveItemsDiscount = 0, fiveItemsDiscountStatus] = useViewFiveItemsDiscountByCustomerId({ customerId });

  const navigate = useNavigate();
  const match = useMatch(`${basePath}/${Routes.SUMMARY}/${Routes.SUMMARY_TABS}`);
  const tab = match?.params.tab;

  const trackPressPricing = useTrackPressPricing({
    page: TrackingPage.SUMMARY,
    country,
    segment,
    tradename,
    checkoutId: checkout?.id,
  });
  const handleOnPricingLayout = useCallback(({ height }: LayoutRectangle) => setPricingHeight(height), []);
  const handleOnPressPricing = useCallback(() => {
    const collapsed = !pricingCollapsed;
    setPricingCollapsed(collapsed);
    trackPressPricing({ collapse: collapsed });
  }, [pricingCollapsed, trackPressPricing]);

  useEffect(() => {
    if (!tab) {
      navigate(
        generatePath(Routes.SUMMARY_TABS, {
          tab: "keep",
        }),
        {
          replace: true,
        },
      );
    }
  }, [navigate, tab]);

  useTrackPageView({
    page: TrackingPage.SUMMARY,
    country,
    segment,
    tradename,
    checkoutId: checkout?.id,
  });

  const trackPressContinue = useTrackPressContinue({
    page: TrackingPage.SUMMARY,
    country,
    segment,
    tradename,
    checkoutId: checkout?.id,
  });
  const handleOnSubmit = useCallback(() => {
    trackPressContinue();
    navigate(`${basePath}/${Routes.CHECKOUT}`);
  }, [basePath, navigate, trackPressContinue]);

  const totalCheckoutItemsKept = useMemo(
    () =>
      checkout?.items.filter(
        (checkoutItem) =>
          checkoutItem.status === CheckoutItemStatus.KEPT || checkoutItem.status === CheckoutItemStatus.REPLACED,
      ).length || 0,
    [checkout?.items],
  );

  const dependenciesLoadedStatuses = [QueryStatus.ERROR, QueryStatus.SUCCESS];
  const dependenciesLoaded =
    dependenciesLoadedStatuses.includes(pricingStatus) && dependenciesLoadedStatuses.includes(fiveItemsDiscountStatus);

  if (!dependenciesLoaded) {
    return <Spinner />;
  }

  return (
    <Layout
      scrollEnabled={false}
      style={{
        header: style.header,
        scrollView: style.scrollView,
      }}
    >
      <ScrollView showsVerticalScrollIndicator={false} testID="summary-view">
        {fiveItemsDiscount !== 0 && <FiveItemsDiscountBanner fiveItemsDiscount={fiveItemsDiscount} />}

        <AuroraLayout
          fullWidth={!isDesktopScreen}
          style={[isDesktopScreen && style.desktopLayoutSpacing, !isDesktopScreen && { paddingBottom: pricingHeight }]}
        >
          <Box size={{ L: "2/3" }} style={isDesktopScreen && style.desktopListSpacing}>
            <View style={[style.contentWrapper, isDesktopScreen && style.desktopContentWrapper]}>
              <View style={!isDesktopScreen && style.mobileInfo}>
                <Text level={3} style={style.title} variant="heading">
                  {titleText}
                </Text>
                <Text level={3} style={style.description}>
                  {descriptionText}
                </Text>
              </View>

              {children}
            </View>
          </Box>

          {pricing && isDesktopScreen ? (
            <Box size={{ L: "1/3" }} style={[style.resume, isDesktopScreen ? style.desktopResume : style.mobileResume]}>
              <View style={style.princingWrapper}>
                <CollapsiblePricing
                  collapsed={false}
                  collapsible={false}
                  pricing={pricing}
                  submitButtonText={submitButtonText}
                  totalCheckoutItemsKept={totalCheckoutItemsKept}
                  onSubmit={handleOnSubmit}
                />
              </View>
            </Box>
          ) : null}
        </AuroraLayout>
      </ScrollView>

      {pricing && !isDesktopScreen ? (
        <Sticky style={style.sticky} onLayout={Platform.OS !== "web" ? handleOnPricingLayout : undefined}>
          <Body>
            <CollapsiblePricing
              collapsed={pricingCollapsed}
              pricing={pricing}
              submitButtonText={submitButtonText}
              totalCheckoutItemsKept={totalCheckoutItemsKept}
              onPress={handleOnPressPricing}
              onSubmit={handleOnSubmit}
            />
          </Body>
        </Sticky>
      ) : null}
    </Layout>
  );
};

export { Summary };
